import React, { useState, useRef, useEffect, useMemo } from "react";
import { faTrashCan, faBusinessTime, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Register icon mappings that are used in the component
const iconMap = {
  faBusinessTime,
  faUser,
};

/**
 * Generic multi-select component that can be used for any entity type
 * @param {Array} availableItems - The available items to select from
 * @param {Array|String} selectedItems - The selected items array
 * @param {Function} setSelectedItems - Optional function to set the selected items
 * @param {Boolean} excludeSelected - The boolean to exclude selected items from the dropdown
 * @param {String} hiddenInputId - The ID of the hidden input field to update
 * @param {String} entityLabel - The label for the entity type (e.g., "Company", "User")
 * @param {String} entityLabelPlural - The plural label (e.g., "Companies", "Users")
 * @param {String} primaryField - The primary field to display (required)
 * @param {String} secondaryField - The optional secondary field to display (Relevant controller must include the field)
 * @param {Array} searchFields - Search fields (defaults to [primaryField, secondaryField] if not provided)
 * @param {Object|String} icon - FontAwesome icon object or string name (e.g., "faBusinessTime", "faUser")
 * @param {String} emptyMessage - Message when no items available
 * @param {Function} fetchItems - Optional function to fetch items (if not provided, uses availableItems prop)
 * @returns {JSX.Element} - The entity multi-select component
 */

const EntityMultiSelect = ({
  availableItems = [],
  selectedItems: propSelectedItems,
  setSelectedItems: propSetSelectedItems,
  excludeSelected,
  hiddenInputId,
  entityLabel = "Item",
  entityLabelPlural = "Items",
  primaryField,
  secondaryField,
  searchFields,
  icon,
  emptyMessage,
  fetchItems,
  compact = false,
  showChevron = false,
  designSystem = false,
}) => {
    const resolvedIcon = typeof icon === 'string' ? iconMap[icon] : icon;
    const resolvedSearchFields = searchFields || [primaryField, secondaryField].filter(Boolean);
    const [multiSelectView, setMultiSelectView] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const prevAvailableItemsIdsRef = useRef('');
    // True while an internal selection change was caused by pulling the parent's value in
    // (the controlled-mode sync effect below), rather than by a user action. Used to skip
    // echoing that change straight back to the parent.
    const syncingFromParentRef = useRef(false);

    const dropdownRef = useRef(null);
    const boxRef = useRef(null);

    // Opt-in flag (Payment Platform MVP) that aligns this multi-select's closed control with the
    // design-system react-select used on the sibling tabs: 41px field, 5px radius,
    // rgba(110,108,113,0.3) hairline border, Ubuntu Light 16px in #6E6C71. Off by default so the
    // legacy (flag-off) Driver Overview and every other caller render exactly as before.
    const ubuntu = "'Ubuntu', system-ui, sans-serif";
    const dsControlStyle = designSystem
      ? {
          height: 41,
          borderRadius: 5,
          padding: '0 10px',
          display: 'flex',
          alignItems: 'center',
          borderColor: multiSelectView ? '#745D96' : 'rgba(110, 108, 113, 0.3)',
          fontFamily: ubuntu,
        }
      : undefined;
    const dsValueStyle = designSystem
      ? {
          color: '#6E6C71',
          fontFamily: ubuntu,
          fontWeight: 300,
          fontSize: 16,
          // Truncate a long fleet name with an ellipsis instead of letting it run under the chevron.
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          paddingRight: 8,
        }
      : undefined;

    const displayEmptyMessage = emptyMessage || `No ${entityLabelPlural} Available.`;

    // Fetch items if fetchItems function is provided
    useEffect(() => {
      if (fetchItems) {
        fetchItems().then(data => {
          if (data && Array.isArray(data)) {
            setItems(data);
          }
        }).catch(error => {
          console.error(`Error fetching ${entityLabelPlural.toLowerCase()}:`, error);
        });
      }
    }, [fetchItems, entityLabelPlural]);

    useEffect(() => {
        if (!hiddenInputId || (!availableItems || availableItems.length === 0) && !fetchItems) return;
        
        const hiddenInput = document.getElementById(hiddenInputId);
        if (!hiddenInput) return;
        
        // Check if we have a prop value (from params)
        if (propSelectedItems) {
            const idsArray = typeof propSelectedItems === 'string' 
            ? propSelectedItems.split(',').map(id => id.trim()).filter(id => id)
            : Array.isArray(propSelectedItems) 
                ? propSelectedItems.map(item => item.id || item).filter(id => id)
                : [];
            
            if (idsArray.length > 0) {
            const sourceItems = fetchItems ? items : availableItems;
            const matchingItems = sourceItems.filter(item => 
                idsArray.includes(item.id.toString())
            );
            if (matchingItems.length > 0) {
                setSelectedItems(matchingItems);
                return;
            }
            }
        }
        
        // Fallback to hidden input value
        const hiddenValue = hiddenInput.value;
        if (hiddenValue) {
            const idsArray = hiddenValue.split(',').map(id => id.trim()).filter(id => id);
            const sourceItems = fetchItems ? items : availableItems;
            const matchingItems = sourceItems.filter(item => 
            idsArray.includes(item.id.toString())
            );
            if (matchingItems.length > 0) {
            setSelectedItems(matchingItems);
            }
        }
    }, [availableItems, hiddenInputId, propSelectedItems, fetchItems, items]);

    // Update hidden field when selectedItems changes
    useEffect(() => {
        if (!hiddenInputId) return;
        
        const hiddenInput = document.getElementById(hiddenInputId);
        if (hiddenInput) {
            if (selectedItems && selectedItems.length > 0) {
            const idsString = selectedItems
                .map(item => item.id || item)
                .filter(id => id)
                .join(',');
            hiddenInput.value = idsString;
            } else {
            hiddenInput.value = '';
            }
            
            // Emit change event for Stimulus to pick up
            hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }, [selectedItems, hiddenInputId]);

    // Call prop setter
    useEffect(() => {
        // When this internal change came from pulling the parent's value in (the sync
        // effect below), don't echo it back to the parent. That echo races the parent's
        // own re-render, so the two mirror effects end up swapping values every commit and
        // spin forever — which is exactly what happened on the Driver Overview fleet select.
        if (syncingFromParentRef.current) {
            syncingFromParentRef.current = false;
            return;
        }
        if (propSetSelectedItems) {
            propSetSelectedItems(selectedItems);
        }
    }, [selectedItems, propSetSelectedItems]);

    // Controlled mode (a setter is provided but there is no hidden input to drive):
    // mirror the parent's selection into the internal state so a parent-driven change
    // — e.g. the Reset button clearing the selection — actually updates the display,
    // instead of the shown "N selected" drifting out of sync with the value the page
    // uses to enable Search. The hidden-input (Stimulus) integration keeps using the
    // effect above. The id-equality check makes this a no-op once the two agree, so it
    // does not loop with the internal->parent setter effect.
    useEffect(() => {
        if (hiddenInputId || !propSetSelectedItems) return;

        const propItems = Array.isArray(propSelectedItems) ? propSelectedItems : [];
        const propIds = propItems.map(item => String(item?.id ?? item)).filter(id => id !== 'null' && id !== 'undefined');
        const sourceItems = fetchItems ? items : availableItems;
        // The set of items the parent's selection actually resolves to here.
        const next = (sourceItems || []).filter(item => propIds.includes(item.id.toString()));

        // Compare as order-independent sets against what is already shown; only re-sync
        // when the selection genuinely differs. This makes the effect idempotent, so it
        // cannot ping-pong with the internal->parent setter effect above (the parent
        // returns new array/object references every render even when nothing changed).
        const setKey = list => [...new Set(list.map(item => String(item?.id)))].sort().join(',');
        if (setKey(next) === setKey(selectedItems)) return;

        // Mark this as a parent-driven change so the internal->parent effect above does not
        // echo it back and start a ping-pong between the two effects.
        syncingFromParentRef.current = true;
        setSelectedItems(next);
    }, [propSelectedItems, availableItems, items, fetchItems, hiddenInputId, propSetSelectedItems, selectedItems]);

    const handleSearchInputChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleClearAll = () => {
        setSelectedItems([]);
    };

    const handleItemSelect = (e, item) => {
        e.stopPropagation();
        const isSelected = selectedItems.some(selectedItem => selectedItem.id === item.id);
        if (isSelected) {
            setSelectedItems(
                selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
            );
        } else {
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleMultiSelectToggle = (e) => {
        e.stopPropagation();
        setMultiSelectView(prev => !prev);
    };

    const handleSelectAll = () => {
        if (selectedItems.length === filteredItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems([...filteredItems]);
        }
    };

    // Load the new availableItems when it changes
    useEffect(() => {
        const sourceItems = fetchItems ? items : availableItems;
        
        if (!sourceItems || sourceItems.length === 0) {
            if (items.length > 0 && !fetchItems) {
                setItems([]);
                prevAvailableItemsIdsRef.current = '';
            }
            return;
        }
        
        const currentIdsStr = sourceItems.map(item => item.id).sort().join(',');
        // Always update on first load (when ref is empty) or when IDs change
        if (prevAvailableItemsIdsRef.current === '' || currentIdsStr !== prevAvailableItemsIdsRef.current) {
            prevAvailableItemsIdsRef.current = currentIdsStr;
            if (!fetchItems) {
              setItems(sourceItems);
            }
        }
    }, [availableItems, fetchItems, items]);

    // Memoize selected item IDs to prevent unnecessary re-renders
    const selectedItemIdsStr = useMemo(() => {
        if (!selectedItems) return '';
        if (typeof selectedItems === 'string') return selectedItems;
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) return '';
        return selectedItems.map(item => item.id || item).sort().join(',');
    }, [selectedItems.length]);
    
    const selectedItemIds = useMemo(() => {
        return new Set(selectedItems.map(item => item.id));
    }, [selectedItemIdsStr]);

    // Compute filtered items using useMemo to prevent unnecessary re-renders
    const filteredItems = useMemo(() => {
        const sourceItems = fetchItems ? items : availableItems;
        if (excludeSelected) {
            return sourceItems.filter(
                (item) => !selectedItemIds.has(item.id)
            );
        }
        return sourceItems;
    }, [items, availableItems, selectedItemIds, excludeSelected, fetchItems]);

    // Filter items by search query
    const filteredItemSelect = useMemo(() => {
        return filteredItems?.filter((item) => {
            const searchTerm = searchQuery.toLowerCase();
            return resolvedSearchFields.some(field => {
                const fieldValue = String(item?.[field] || '').toLowerCase();
                return fieldValue.includes(searchTerm);
            });
        });
    }, [filteredItems, searchQuery, resolvedSearchFields]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                boxRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !boxRef.current.contains(event.target)
            ) {
                setMultiSelectView(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const sourceItems = fetchItems ? items : availableItems;

    return (
        <>
            <div
                className={`companySelect block appearance-none w-full bg-white border ${designSystem ? '' : 'text-gray-700'} ${compact ? (designSystem ? '' : 'px-3 py-3 text-sm') : 'pt-3 pb-2 px-4'} ${designSystem ? '' : 'rounded-md'} leading-tight focus:outline-none focus:bg-white
                    ${designSystem ? '' : (multiSelectView ? 'border-gray-500' : 'border-gray-300')}`}
                style={dsControlStyle}>

                {/* No items available */}
                {sourceItems?.length === 0 && (
                    <div className={`w-full bg-white ${compact ? '' : 'rounded-lg p-1'} flex items-center justify-between focus:border-gray-500`}>
                        <span className={`${compact ? '' : 'ml-1'} ${designSystem ? '' : 'text-gray-600'}`} style={dsValueStyle}>
                            {displayEmptyMessage}
                        </span>
                    </div>
                )}

                {/* Items available */}
                {sourceItems?.length !== 0 && (
                    <div
                        className={`w-full bg-white ${compact ? '' : 'rounded-lg p-1'} flex items-center justify-between focus:border-gray-500`}
                        onClick={(e) => {
                            handleMultiSelectToggle(e);
                        }}
                        style={{ userSelect: "none" }}
                        ref={boxRef}
                    >
                        <span className={designSystem ? '' : 'ml-1 text-gray-600'} style={dsValueStyle}>
                            {(() => {
                                if (selectedItems?.length === 0) return entityLabel;
                                if (selectedItems?.length === 1) return selectedItems[0]?.[primaryField] || entityLabel;
                                return `${selectedItems?.length} ${entityLabelPlural.toLowerCase()} selected`;
                            })()}
                        </span>
                        <div className="flex items-center gap-2">
                            {resolvedIcon && <FontAwesomeIcon icon={resolvedIcon} className="text-gray-500" />}
                            {showChevron && (
                                <svg className={`w-4 h-4 ${designSystem ? '' : 'text-gray-400'} transition-transform ${multiSelectView ? 'rotate-180' : ''}`} style={designSystem ? { color: '#6E6C71' } : undefined} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            )}
                        </div>
                        {multiSelectView && (
                            <div
                                ref={dropdownRef}
                                className="absolute top-full left-0 bg-white shadow-md w-full dark-gray-scrollbar overflow-hidden p-3 z-40 rounded-md"
                                style={{
                                    top: "calc(100% + 8px)",
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                }}
                            >

                                <div className="flex justify-between items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectAll()}
                                        className="text-xs text-purple-500 p-1 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                    >
                                        <em>
                                            {selectedItems.length > 0 && sourceItems.length > 0 && selectedItems.length === sourceItems.length
                                                ? "Deselect All"
                                                : "Select All"}
                                        </em>
                                    </button>

                                    <button
                                        onClick={handleClearAll}
                                        className="text-xs text-gray-600"
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} className="text-red-400 " />
                                    </button>
                                </div>

                                <input
                                    type="text"
                                    className="w-full px-2 py-1 text-xs bg-white text-gray-600 border border-gray-300 rounded-lg"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={handleSearchInputChange}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    autoFocus={true}
                                />
                                <div
                                    className={`pb-1 bg-white overflow-y-auto dark-gray-scrollbar ${designSystem ? 'mt-2' : ''}`}
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="options-menu"
                                >
                                    {filteredItemSelect?.map((item) => {
                                        const isSelected = selectedItems.some(selectedItem => selectedItem.id === item.id);
                                        return (
                                            <div
                                                key={item.id}
                                                className={`block px-2 py-2 text-xs cursor-pointer ${designSystem
                                                    ? (isSelected ? '' : 'hover:bg-gray-100')
                                                    : `text-gray-600 bg-white hover:bg-blue-100 ${isSelected ? 'bg-purple-200 text-gray-600' : ''}`
                                                    }`}
                                                style={designSystem
                                                    ? { color: '#6E6C71', fontFamily: ubuntu, ...(isSelected ? { backgroundColor: '#EDE9F3' } : {}) }
                                                    : undefined}
                                                onClick={(e) => handleItemSelect(e, item)}
                                            >
                                                <p className="p-0 m-0">
                                                    {item[primaryField]}
                                                </p>
                                                {secondaryField && (
                                                    <p className="p-0 m-0 text-xs">
                                                        {item[secondaryField] || ''}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default EntityMultiSelect;
